import {InlineLink, SubNav, ThemeProvider, type ColorMode} from '@primer/react-brand'
import {clsx} from 'clsx'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import Markdown, {type Components} from 'react-markdown'
import {WebGLPage} from '@github-ui/brand-webgl'
import type {OS} from '@github-ui/get-os'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

import {CtaBanner} from './components/CtaBanner/CtaBanner'
import {Hero} from './components/Hero/Hero'
import {Features} from './components/Features/Features'
import {CardsSection} from './components/CardsSection/CardsSection'
import {Pricing} from './components/Pricing/Pricing'

import {COLOR_MODE, getCopy} from './Index.data'

import Styles from './Index.module.css'

const PLAN_MARKDOWN_COMPONENTS_BASE: Components = {
  a: ({href, children}) => <InlineLink href={href ?? '#'}>{children}</InlineLink>,
  p: ({children}) => <>{children} </>,
}

const PLAN_FOOTNOTE_MARKDOWN_COMPONENTS: Components = {
  ...PLAN_MARKDOWN_COMPONENTS_BASE,
}

const PLAN_ACTIONS_MESSAGE_MARKDOWN_COMPONENTS: Components = {
  ...PLAN_MARKDOWN_COMPONENTS_BASE,
}

const PLAN_FEATURES_ITEM_MARKDOWN_COMPONENTS: Components = {
  ...PLAN_MARKDOWN_COMPONENTS_BASE,
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IS_STORYBOOK = (import.meta as any).env?.STORYBOOK

export interface GitHubAppGAPageProps {
  initialOS?: OS
}

interface GitHubAppGAPageRoutePayload {
  initialOS?: OS
}

const GitHubAppGAPageContent = ({initialOS}: GitHubAppGAPageProps) => {
  const copy = getCopy()
  const colorMode: ColorMode = COLOR_MODE ?? 'auto'

  return (
    <ThemeProvider colorMode={colorMode} className={Styles['GitHubAppGAPage']}>
      <WebGLPage {...(colorMode === 'auto' ? {} : {mode: colorMode})}>
        <SubNav className={Styles['GitHubAppGAPage__subNav']}>
          <SubNav.Heading
            href={copy.subNav.heading.href}
            {...getAnalyticsEvent({
              action: copy.subNav.heading.label,
              tag: 'a',
              context: 'subnav_heading',
              location: 'subnav',
            })}
          >
            {copy.subNav.heading.label}
          </SubNav.Heading>
          {copy.subNav.items.map((link, index) => (
            <SubNav.Link
              key={`link-${index}`}
              href={link.href}
              aria-current={link.isSelected ? 'page' : undefined}
              {...getAnalyticsEvent({action: link.label, tag: 'a', context: 'subnav', location: 'subnav'})}
            >
              {link.label}
            </SubNav.Link>
          ))}
        </SubNav>

        <Hero {...copy.hero} initialOS={initialOS} />

        <Features {...copy.features} initialOS={initialOS} />
        <CardsSection {...copy.tools} location="tools" />

        <div className={clsx(Styles['GitHubAppGAPage__spacer'], Styles['GitHubAppGAPage__spacer--mobileOnly'])} />

        <Pricing
          {...copy.pricing}
          plans={copy.pricing.plans.map(plan => ({
            ...plan,
            footnote: plan.footnote ? (
              <Markdown components={PLAN_FOOTNOTE_MARKDOWN_COMPONENTS}>{plan.footnote}</Markdown>
            ) : undefined,
            actionsMessage: plan.actionsMessage
              ? {
                  ...plan.actionsMessage,
                  content: (
                    <Markdown components={PLAN_ACTIONS_MESSAGE_MARKDOWN_COMPONENTS}>
                      {plan.actionsMessage.content}
                    </Markdown>
                  ),
                }
              : undefined,
            features: {
              ...plan.features,
              items: plan.features.items.map(item => ({
                ...item,
                label: <Markdown components={PLAN_FEATURES_ITEM_MARKDOWN_COMPONENTS}>{item.label}</Markdown>,
              })),
            },
          }))}
        />

        <div className={Styles['GitHubAppGAPage__spacer']} />

        <CtaBanner {...copy.ctaBanner} initialOS={initialOS} />

        <CardsSection {...copy.resources} location="resources" />

        <div className={Styles['GitHubAppGAPage__spacer']} />
      </WebGLPage>
    </ThemeProvider>
  )
}

const GitHubAppGAPageWithRoutePayload = ({initialOS: initialOSOverride}: GitHubAppGAPageProps) => {
  const routePayload = useRoutePayload<GitHubAppGAPageRoutePayload | null>()

  return <GitHubAppGAPageContent initialOS={initialOSOverride ?? routePayload?.initialOS} />
}

export const GitHubAppGAPage = (props: GitHubAppGAPageProps) => {
  if (IS_STORYBOOK) return <GitHubAppGAPageContent {...props} />

  return <GitHubAppGAPageWithRoutePayload {...props} />
}
